/* eslint-disable no-undef */
import { ref, computed, onBeforeUnmount } from 'vue';
import type { Ref, ComputedRef } from 'vue';

/** Map SpeechRecognition error codes to user-friendly messages */
function getErrorMessage(errorCode: string): string {
  const messages: Record<string, string> = {
    'not-allowed': 'Microphone access was denied. Please allow microphone permissions.',
    'no-speech': 'No speech was detected. Please try again.',
    network: 'A network error occurred. Please check your connection.',
    'audio-capture': 'No microphone was found. Please ensure a microphone is connected.',
    aborted: 'Speech recognition was aborted.',
  };
  return messages[errorCode] || 'An error occurred with speech recognition.';
}

interface UseSpeechRecognitionReturn {
  isSupported: ComputedRef<boolean>;
  isListening: Ref<boolean>;
  transcript: Ref<string>;
  error: Ref<string | null>;
  start: () => void;
  stop: () => void;
}

/**
 * Vue 3 composable wrapping the Web Speech API for voice-to-text input.
 * Continuous mode: stays active until user clicks stop.
 * Hidden on unsupported browsers via `isSupported`.
 */
export function useSpeechRecognition(lang = 'en-US'): UseSpeechRecognitionReturn {
  const isListening = ref(false);
  const transcript = ref('');
  const error = ref<string | null>(null);

  let recognition: SpeechRecognition | null = null;

  const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
  const isSupported = computed(() => !!SpeechRecognitionAPI);

  function start(): void {
    if (!SpeechRecognitionAPI || isListening.value) return;

    error.value = null;
    recognition = new SpeechRecognitionAPI();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onresult = (event: SpeechRecognitionEvent): void => {
      const result = event.results[event.results.length - 1];
      if (result.isFinal) {
        transcript.value = result[0].transcript.trim();
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent): void => {
      // 'aborted' fires when stop() is called manually — not a real error
      if (event.error !== 'aborted') {
        error.value = getErrorMessage(event.error);
      }
      isListening.value = false;
    };

    recognition.onend = (): void => {
      isListening.value = false;
      recognition = null;
    };

    recognition.start();
    isListening.value = true;
  }

  function stop(): void {
    if (recognition && isListening.value) {
      recognition.stop();
    }
  }

  onBeforeUnmount((): void => {
    stop();
  });

  return { isSupported, isListening, transcript, error, start, stop };
}
